import { ReactElement, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ProductsAnalyticsProvider } from '@client/analytics/context/products/ProductsAnalyticsContext';
import { SearchAnalyticsProvider } from '@client/analytics/context/search/SearchAnalyticsContext';
import {
  usePageViewAnalytics,
  useProductNotFoundPageViewAnalytics,
  useRemoteRecordsAnalytics,
} from '@client/analytics/hooks';
import { analyzeProductsListView } from '@client/analytics/redux';
import { EVENT_SOURCE, TEALIUM_PAGE_TYPE } from '@client/analytics/types/types';
import { useBreadcrumbs } from '@client/breadcrumbs/hooks';
import { BrandPage as BrandPageComponent } from '@client/catalog/components/BrandPage/Component/BrandPage';
import { useSearchData } from '@client/catalog/hooks';
import { getCrumbsFromCategories } from '@client/catalog/utils/crumbs';
import { getBrandFilterFromSearchParams } from '@client/catalog/utils/filters';
import { useProductsTiles } from '@client/product/hooks';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useRouterPush, useUrlCreator } from '@client/routes/hooks';

export const BrandPage = (): ReactElement => {
  const push = useRouterPush();
  const urlCreator = useUrlCreator();
  const history = useHistory();
  const brand = getBrandFilterFromSearchParams();
  const catalogRecord = useSearchData();
  const productTiles = useProductsTiles();
  const analyzeProductNotFoundPageView = useProductNotFoundPageViewAnalytics();

  const shouldRedirectToHomepage = !brand && history.action !== 'POP';
  const homepageUrl = urlCreator(ROUTES.homepage);
  const catalog = catalogRecord.data;
  const selectedCategories = catalog?.navigation.selected || [];
  const skus = catalog?.products.map(product => product.sku) || [];

  useEffect(() => {
    if (shouldRedirectToHomepage) {
      push(homepageUrl);
    }
  }, [shouldRedirectToHomepage, homepageUrl]);

  const handleProductsNotFound = () => {
    analyzeProductNotFoundPageView();
  };

  usePageViewAnalytics({
    remoteRecord: catalogRecord,
    pageType: TEALIUM_PAGE_TYPE.brand,
  });

  useRemoteRecordsAnalytics(
    [
      catalogRecord,
      ...skus.map(sku => productTiles[sku]),
    ],
    () => analyzeProductsListView({
      eventSource: EVENT_SOURCE.brand,
    }),
  );

  useBreadcrumbs([
    ...getCrumbsFromCategories(selectedCategories, urlCreator),
    { title: brand },
  ]);

  return (
    <ProductsAnalyticsProvider eventSource={EVENT_SOURCE.brand}>
      <SearchAnalyticsProvider>
        <BrandPageComponent
          title={brand}
          isBrandVisible={selectedCategories.length === 0}
          onProductNotFound={handleProductsNotFound}
        />
      </SearchAnalyticsProvider>
    </ProductsAnalyticsProvider>
  );
};
